<template>
  <v-dialog v-model="dialog" :max-width="$vuetify.breakpoint.mobile ? '95vw' : '800px'" persistent :fullscreen="$vuetify.breakpoint.xsOnly">
    <v-card class="sync-guide-chat" :class="{ 'fullscreen-mode': $vuetify.breakpoint.xsOnly }">
      <v-card-title class="headline d-flex justify-space-between align-center orange darken-2 white--text fixed-header">
        <div class="d-flex align-center">
          <span class="text-h6">SyncGuideX™</span>
        </div>
        <v-btn icon @click="closeDialog" class="white--text">
          <v-icon class="material-icons-round">close</v-icon>
        </v-btn>
      </v-card-title>

      <v-card-text class="pa-0 chat-content">
        <!-- Chat Messages Area -->
        <div class="chat-container" ref="chatContainer">
          <div class="welcome-message pa-4">
            <div class="ai-message">
              <v-avatar size="40" class="mr-3 orange darken-2">
                <v-icon class="white--text material-icons-round">smart_toy</v-icon>
              </v-avatar>
              <div class="message-bubble ai-bubble">
                <p class="mb-2"><strong>Welcome to SyncGuideX™!</strong></p>
                <p class="mb-1">I'm your AI-powered vehicle search assistant. Tell me what you're looking for in natural language!</p>
                <p class="mb-1"><strong>Examples:</strong></p>
                <ul class="mb-0">
                  <li>"Find me a 2019 Toyota under $15k"</li>
                </ul>
              </div>
            </div>
          </div>

          <!-- Chat Messages -->
          <div v-for="(message, index) in messages" :key="index" class="message pa-3">
            <div v-if="message.type === 'user'" class="user-message">
              <div class="message-bubble user-bubble">
                {{ message.text }}
              </div>
              <v-avatar size="40" class="ml-3 grey">
                <v-icon class="white--text material-icons-round" size="20">person</v-icon>
              </v-avatar>
            </div>
            <div v-else class="ai-message">
              <v-avatar size="40" class="mr-3 orange darken-2">
                <v-icon class="white--text material-icons-round" size="20">smart_toy</v-icon>
              </v-avatar>
              <div class="message-bubble ai-bubble">
                <div v-if="message.loading" class="loading-container">
                  <div class="loading-text">{{ processingText }}</div>
                  <div class="loading-dots">
                    <span></span><span></span><span></span>
                  </div>
                </div>
                <div v-else>
                  <p v-if="message.text" class="mb-2">{{ message.text }}</p>
                  <div v-if="message.filters && hasValidFilters(message.filters)" class="extracted-filters">
                    <!-- Only show criteria chips if there are no suggested vehicles and we have valid filters -->
                    <div v-if="!message.filters.suggested_vehicles || message.filters.suggested_vehicles.length === 0">
                      <p class="mb-2"><strong>I found these search criteria:</strong></p>
                      <v-chip-group column>
                        <v-chip v-if="message.filters.make" small color="primary" outlined>
                          Make: {{ message.filters.make }}
                        </v-chip>
                        <v-chip v-if="message.filters.model" small color="primary" outlined>
                          Model: {{ message.filters.model }}
                        </v-chip>
                        <v-chip v-if="message.filters.year" small color="primary" outlined>
                          Year: {{ message.filters.year }}
                        </v-chip>

                         <v-chip v-if="message.filters.price_min" small color="primary" outlined>
                          Min Price: ${{ message.filters.price_min.toLocaleString() }}
                        </v-chip>
                         <v-chip v-if="message.filters.price_max" small color="primary" outlined>
                          Max Price: ${{ message.filters.price_max.toLocaleString() }}
                        </v-chip>
                        <v-chip v-if="message.filters.location" small color="primary" outlined>
                          Location: {{ message.filters.location }}
                        </v-chip>
                        <v-chip v-if="message.filters.body_style" small color="primary" outlined>
                          Type: {{ message.filters.body_style }}
                        </v-chip>
                      </v-chip-group>
                    </div>

                    <!-- Show comparison vehicle suggestions -->
                    <div v-if="message.filters.comparison_vehicles && message.filters.comparison_vehicles.length > 0" class="mt-3">
                      <div class="comparison-suggestion-card">
                        <v-card outlined class="pa-3">
                          <div class="d-flex align-center mb-3">
                            <h4 class="mb-0">Similar Vehicle Suggestions</h4>
                          </div>

                          <p class="mb-3">{{ message.filters.comparison_message }}</p>

                          <div class="similar-vehicles-preview mb-3">
                            <v-chip
                              v-for="vehicle in message.filters.comparison_vehicles.slice(0, 3)"
                              :key="`${vehicle.make}-${vehicle.model}`"
                              class="mr-2 mb-2"
                              outlined
                              small
                            >
                              {{ vehicle.make }} {{ vehicle.model }}
                            </v-chip>
                          </div>

                          <div class="d-flex">
                            <v-btn
                              color="orange"
                              dark
                              @click="showComparison(message.filters)"
                              :loading="loadingComparison"
                              class="mr-2"
                            >
                              Yes, Show Me
                            </v-btn>
                            <v-btn
                              outlined
                              @click="searchWithFilters(message.filters)"
                              :loading="searching"
                            >
                              Just Show {{ message.filters.make }} {{ message.filters.model }}
                            </v-btn>
                          </div>
                        </v-card>
                      </div>
                    </div>

                    <div v-if="hasValidFilters(message.filters)" class="mt-3">
                      <v-btn
                        color="green"
                        class="mr-2"
                        dark
                        @click="searchWithFilters(message.filters)"
                        :loading="searching"
                      >
                        Show Vehicles
                      </v-btn>
                      <!-- <v-btn
                        v-if="message.filters.suggested_vehicles"
                        color="orange"
                        outlined
                        @click="searchWithoutSuggestions(message.filters)"
                        :loading="searching"
                      >
                        Show Without Suggestions
                      </v-btn> -->
                    </div>
                  </div>
                </div>
              </div>
            </div>
          </div>
        </div>
      </v-card-text>

      <!-- Input Area -->
      <v-divider></v-divider>
      <v-card-actions class="input-area pa-4 fixed-footer">
        <div class="textarea-container">
          <v-textarea
            v-model="userInput"
            :label="isListening ? 'Listening...' : 'Type your query or click mic to speak...'"
            outlined
            :dense="$vuetify.breakpoint.mdAndUp"
            :hide-details="$vuetify.breakpoint.mdAndUp"
            @keyup.enter.exact="sendMessage"
            @keyup.shift.enter="addNewLine"
            :disabled="processing"
            class="input-field"
            rows="2"
            auto-grow
            :max-rows="4"
          ></v-textarea>

          <!-- Buttons Container -->
          <div class="buttons-container">
            <!-- Microphone Button -->
            <v-btn
              :color="isListening ? 'red' : 'grey'"
              @click="toggleSpeechRecognition"
              :disabled="processing"
              class="mic-btn"
              icon
              small
              :class="{ 'pulse-animation': isListening }"
            >
              <v-icon class="material-icons-round" size="20">
                {{ isListening ? 'mic' : 'mic_none' }}
              </v-icon>
            </v-btn>

            <!-- Send Button -->
            <v-btn
              color="white"
              @click="sendMessage"
              dark
              :loading="processing"
              class="send-btn"
              icon
              small
            >
              <v-icon class="material-icons-round" size="20">send</v-icon>
            </v-btn>
          </div>
        </div>
      </v-card-actions>
    </v-card>
  </v-dialog>
</template>

<script>
export default {
  name: 'SyncGuideXChat',
  props: {
    value: {
      type: Boolean,
      default: false
    }
  },
  data() {
    return {
      dialog: this.value,
      userInput: '',
      processing: false,
      searching: false,
      loadingComparison: false,
      messages: [],
      isListening: false,
      recognition: null,
      speechSupported: false,
      processingText: 'Processing...',
      processingTexts: [
        'Understanding your request...',
        'Analyzing your query...',
        'Processing natural language...',
        'Extracting search criteria...',
        'Preparing your results...',
        'Almost done...'
      ],
      processingTextIndex: 0,
      processingInterval: null
    };
  },
  watch: {
    value(newVal) {
      this.dialog = newVal;
    },
    dialog(newVal) {
      this.$emit('input', newVal);
      if (newVal) {
        this.$nextTick(() => {
          this.scrollToBottom();
        });
      } else {
        // Reset speech recognition when dialog closes
        this.stopSpeechRecognition();
      }
    }
  },
  mounted() {
    this.initializeSpeechRecognition();
  },
  beforeDestroy() {
    // Clean up speech recognition
    this.stopSpeechRecognition();
    // Clean up processing animation
    this.stopProcessingAnimation();
  },
  methods: {
    closeDialog() {
      this.dialog = false;
    },

    addNewLine() {
      // Allow shift+enter to add new lines in textarea
      this.userInput += '\n';
    },

    startProcessingAnimation() {
      this.processingTextIndex = 0;
      this.processingText = this.processingTexts[0];

      this.processingInterval = setInterval(() => {
        this.processingTextIndex = (this.processingTextIndex + 1) % this.processingTexts.length;
        this.processingText = this.processingTexts[this.processingTextIndex];
      }, 1200); // Change text every 1.2 seconds
    },

    stopProcessingAnimation() {
      if (this.processingInterval) {
        clearInterval(this.processingInterval);
        this.processingInterval = null;
      }
      this.processingText = 'Processing...';
    },

    async sendMessage() {
      if (!this.userInput.trim()) return;

      const userMessage = this.userInput.trim();
      this.userInput = '';

      // Add user message
      this.messages.push({
        type: 'user',
        text: userMessage,
        timestamp: new Date()
      });

      // Add loading AI message
      const loadingMessage = {
        type: 'ai',
        loading: true,
        timestamp: new Date()
      };
      this.messages.push(loadingMessage);

      this.processing = true;
      this.startProcessingAnimation();
      this.scrollToBottom();

      try {
        // Call Laravel backend which will process NLP via scrap-node
        const response = await axios.post('/api/syncguidex/process-query', {
          query: userMessage
        });

        // Remove loading message and stop animation
        this.messages.pop();
        this.stopProcessingAnimation();

        if (response.data.flag === 1) {
          const queryType = response.data.query_type;

          if (queryType === 'search' || queryType === 'search_with_comparison') {
            // Add AI response with extracted filters for search queries
            this.messages.push({
              type: 'ai',
              text: response.data.message || "I've analyzed your request and extracted the following search criteria:",
              filters: response.data.filters,
              timestamp: new Date()
            });
          } else if (queryType === 'information') {
            // Add informational response
            this.messages.push({
              type: 'ai',
              text: response.data.message,
              timestamp: new Date()
            });
          } else if (queryType === 'non_vehicle') {
            // Add non-vehicle response
            this.messages.push({
              type: 'ai',
              text: response.data.message,
              timestamp: new Date()
            });
          } else {
            // Fallback for unknown types
            this.messages.push({
              type: 'ai',
              text: response.data.message || "I'm not sure how to help with that request.",
              timestamp: new Date()
            });
          }
        } else {
          // Add error message
          this.messages.push({
            type: 'ai',
            text: response.data.message || "I couldn't understand your request. Please try rephrasing it or use the traditional search filters.",
            timestamp: new Date()
          });
        }

      } catch (error) {
        console.error('Error processing message:', error);

        // Remove loading message and stop animation
        this.messages.pop();
        this.stopProcessingAnimation();

        // Add error message
        this.messages.push({
          type: 'ai',
          text: "I'm sorry, I'm having trouble processing your request right now. Please try again or use the traditional search filters.",
          timestamp: new Date()
        });
      }

      this.processing = false;
      this.scrollToBottom();
    },

    async searchWithFilters(filters) {
      this.searching = true;

      try {
        // Convert SyncGuideX filters to the format expected by existing search
        const searchParams = this.convertFiltersToSearchParams(filters);

        // Clean up null/empty values to prevent issues
        Object.keys(searchParams).forEach(key => {
          if (searchParams[key] === null || searchParams[key] === undefined || searchParams[key] === '') {
            delete searchParams[key];
          }
        });

        console.log('SyncGuideX Search Params:', searchParams);

        // Emit search event with converted filters
        this.$emit('search', searchParams);

        // Close dialog after initiating search
        setTimeout(() => {
          this.searching = false;
          this.closeDialog();
        }, 1000);

      } catch (error) {
        console.error('Error searching with filters:', error);
        this.searching = false;
      }
    },



    convertFiltersToSearchParams(filters) {
      // Convert SyncGuideX filters to existing search parameter format
      const searchParams = {
        make: filters.make || '',
        model: filters.model || '',
        year: filters.year || '',
        price_min: filters.price_min || '',
        price_max: filters.price_max || '',
        zip_code: filters.zip_code || '',
        radius: filters.radius || 50,
        bodyStyle: filters.body_style || filters.purpose || ''
      };

      // Include suggested vehicles for intelligent search
      if (filters.suggested_vehicles && filters.suggested_vehicles.length > 0) {
        // Parse suggested vehicles into makes and models
        const makes = [];
        const models = [];

        filters.suggested_vehicles.forEach(vehicle => {
          const vehicleName = this.getVehicleName(vehicle);
          const parts = vehicleName.split(' ');
          if (parts.length >= 2) {
            const make = parts[0];
            const model = parts.slice(1).join(' ');
            if (!makes.includes(make)) makes.push(make);
            if (!models.includes(model)) models.push(model);
          }
        });

        // Override make and model with suggested vehicles data
        if (makes.length > 0) {
          searchParams.makes = makes;
          // If there's only one make, also set the single make field
          if (makes.length === 1) {
            searchParams.make = makes[0];
          }
        }
        if (models.length > 0) {
          searchParams.models = models;
          // If there's only one model, also set the single model field
          if (models.length === 1) {
            searchParams.model = models[0];
          }
        }
      }



      return searchParams;
    },

    async searchWithSpecificVehicle(vehicle, originalFilters) {
      this.searching = true;

      try {
        // Parse vehicle name into make and model
        const parts = vehicle.split(' ');
        const make = parts[0];
        const model = parts.slice(1).join(' ');

        // Create new filters with specific vehicle
        const specificFilters = {
          ...originalFilters,
          make: make,
          model: model,
          suggested_vehicles: undefined // Remove suggestions to focus on specific vehicle
        };

        const searchParams = this.convertFiltersToSearchParams(specificFilters);
        this.$emit('search', searchParams);

        setTimeout(() => {
          this.searching = false;
          this.closeDialog();
        }, 1000);

      } catch (error) {
        console.error('Error searching with specific vehicle:', error);
        this.searching = false;
      }
    },

    async searchWithoutSuggestions(originalFilters) {
      this.searching = true;

      try {
        // Create filters without suggestions
        const basicFilters = {
          ...originalFilters,
          suggested_vehicles: undefined
        };

        const searchParams = this.convertFiltersToSearchParams(basicFilters);
        this.$emit('search', searchParams);

        setTimeout(() => {
          this.searching = false;
          this.closeDialog();
        }, 1000);

      } catch (error) {
        console.error('Error searching without suggestions:', error);
        this.searching = false;
      }
    },

    async showComparison(filters) {
      this.loadingComparison = true;

      try {
        // Get base search parameters
        const baseParams = this.convertFiltersToSearchParams(filters);

        // Extract all makes and models for comparison
        const allMakes = [filters.make].filter(Boolean);
        const allModels = [filters.model].filter(Boolean);

        // Add comparison vehicles to the search
        if (filters.comparison_vehicles && Array.isArray(filters.comparison_vehicles)) {
          filters.comparison_vehicles.forEach(vehicle => {
            if (vehicle.make && !allMakes.includes(vehicle.make)) {
              allMakes.push(vehicle.make);
            }
            if (vehicle.model && !allModels.includes(vehicle.model)) {
              allModels.push(vehicle.model);
            }
          });
        }

        // Create search parameters that include both the main vehicle and comparison vehicles
        const comparisonParams = {
          ...baseParams,
          comparison_mode: true,
          main_vehicle: {
            make: filters.make,
            model: filters.model
          },
          comparison_vehicles: filters.comparison_vehicles,
          // Include all makes and models as arrays (required by InventoryFilter)
          makes: allMakes,
          models: allModels,
          // Override single make/model if we have multiple
          make: allMakes.length === 1 ? allMakes[0] : '',
          model: allModels.length === 1 ? allModels[0] : '',
          // Add original vehicle info for comparison tracking
          original_make: filters.make,
          original_model: filters.model
        };

        console.log('🔍 Comparison search params:', comparisonParams);

        // Emit search event with comparison mode enabled
        this.$emit('search', comparisonParams);

        // Close dialog after initiating comparison search
        setTimeout(() => {
          this.loadingComparison = false;
          this.closeDialog();
        }, 1000);

      } catch (error) {
        console.error('Error showing comparison:', error);
        this.loadingComparison = false;
      }
    },

    getVehicleName(vehicle) {
      // Handle both string and object formats
      if (typeof vehicle === 'string') {
        return vehicle;
      } else if (typeof vehicle === 'object' && vehicle.name) {
        return vehicle.name;
      }
      return 'Unknown Vehicle';
    },

    getVehicleDescription(vehicle) {
      // Handle both string and object formats
      if (typeof vehicle === 'object' && vehicle.description) {
        return vehicle.description;
      }
      return 'No description available';
    },

    scrollToBottom() {
      this.$nextTick(() => {
        const container = this.$refs.chatContainer;
        if (container) {
          container.scrollTop = container.scrollHeight;
        }
      });
    },

    hasValidFilters(filters) {
      if (!filters) return false;

      // Check if any meaningful search criteria exist
      const validFields = [
        'make', 'model', 'year', 'price_min', 'price_max',
        'location', 'zip_code', 'body_style', 'purpose'
      ];

      return validFields.some(field =>
        filters[field] &&
        filters[field] !== '' &&
        filters[field] !== null &&
        filters[field] !== undefined
      );
    },

    initializeSpeechRecognition() {
      // Check if speech recognition is supported
      if ('webkitSpeechRecognition' in window || 'SpeechRecognition' in window) {
        this.speechSupported = true;

        // Create speech recognition instance
        const SpeechRecognition = window.SpeechRecognition || window.webkitSpeechRecognition;
        this.recognition = new SpeechRecognition();

        // Configure speech recognition
        this.recognition.continuous = false;
        this.recognition.interimResults = false;
        this.recognition.lang = 'en-US';

        // Handle speech recognition results
        this.recognition.onresult = (event) => {
          try {
            const transcript = event.results[0][0].transcript;
            this.userInput = transcript;
            this.isListening = false;

            // Auto-send the message after speech recognition
            setTimeout(() => {
              if (this.userInput.trim()) {
                this.sendMessage();
              }
            }, 500);
          } catch (error) {
            console.error('Error processing speech result:', error);
            this.isListening = false;
          }
        };

        // Handle speech recognition errors
        this.recognition.onerror = (event) => {
          console.error('Speech recognition error:', event.error);
          this.isListening = false;

          // Show user-friendly error message
          if (event.error === 'no-speech') {
            this.showToast('No speech detected. Please try again.', 'warning');
          } else if (event.error === 'not-allowed') {
            this.showToast('Microphone access denied. Please allow microphone access and try again.', 'error');
          } else if (event.error === 'aborted') {
            // Don't show error for aborted (user stopped)
            console.log('Speech recognition aborted by user');
          } else {
            this.showToast('Speech recognition failed. Please try again.', 'error');
          }
        };

        // Handle speech recognition end
        this.recognition.onend = () => {
          // Always ensure state is reset when recognition ends
          this.isListening = false;
        };

        // Handle speech recognition start
        this.recognition.onstart = () => {
          this.isListening = true;
        };

      } else {
        this.speechSupported = false;
        console.warn('Speech recognition not supported in this browser');
      }
    },

    toggleSpeechRecognition() {
      if (!this.speechSupported) {
        this.showToast('Speech recognition is not supported in your browser.', 'error');
        return;
      }

      if (this.isListening) {
        this.stopSpeechRecognition();
      } else {
        this.startSpeechRecognition();
      }
    },

    startSpeechRecognition() {
      if (!this.speechSupported || !this.recognition) {
        return;
      }

      try {
        // Ensure we're not already listening
        if (this.isListening) {
          this.stopSpeechRecognition();
        }

        this.recognition.start();
      } catch (error) {
        console.error('Error starting speech recognition:', error);
        this.isListening = false;
        this.showToast('Failed to start speech recognition. Please try again.', 'error');
      }
    },

    stopSpeechRecognition() {
      if (this.recognition && this.isListening) {
        try {
          this.recognition.stop();
        } catch (error) {
          console.error('Error stopping speech recognition:', error);
        }
      }
      // Always reset the state
      this.isListening = false;
    },

    showToast(message, type = 'info') {
      // Try to use Vue Toast if available, otherwise use alert
      if (this.$toast) {
        if (type === 'error') {
          this.$toast.error(message, { position: 'top-right' });
        } else if (type === 'warning') {
          this.$toast.warning(message, { position: 'top-right' });
        } else {
          this.$toast.info(message, { position: 'top-right' });
        }
      } else {
        // Fallback to alert
        alert(message);
      }
    }
  }
};
</script>

<style scoped>
.sync-guide-chat {
  height: 600px;
  display: flex;
  flex-direction: column;
}

.chat-container {
  height: 450px;
  overflow-y: auto;
  background-color: #f5f5f5;
}

/* Fullscreen mode styling */
.fullscreen-mode {
  display: flex !important;
  flex-direction: column !important;
  height: 100vh !important;
  max-height: 100vh !important;
}

.fullscreen-mode .fixed-header {
  position: fixed !important;
  top: 0 !important;
  left: 0 !important;
  right: 0 !important;
  z-index: 10 !important;
  width: 100% !important;
  margin: 0 !important;
}

.fullscreen-mode .chat-content {
  flex: 1 !important;
  overflow: hidden !important;
  margin-top: 64px !important;
  margin-bottom: 140px !important;
}

.fullscreen-mode .chat-container {
  height: 100% !important;
  overflow-y: auto !important;
}

.fullscreen-mode .fixed-footer {
  position: fixed !important;
  bottom: 0 !important;
  left: 0 !important;
  right: 0 !important;
  z-index: 10 !important;
  background-color: white !important;
  border-top: 1px solid #e0e0e0 !important;
  margin: 0 !important;
  width: 100% !important;
  box-sizing: border-box !important;
}

/* Mobile responsive layout */
@media (max-width: 768px) {
  .sync-guide-chat {
    height: 100vh;
    max-height: 100vh;
  }

  .chat-container {
    height: calc(100vh - 200px);
    flex: 1;
  }

  .v-card-title {
    padding: 12px 16px !important;
  }

  .v-card-title .text-h6 {
    font-size: 18px !important;
  }

  /* Fullscreen adjustments for mobile */
  .fullscreen-mode .chat-content {
    margin-top: 56px !important;
    margin-bottom: 120px !important;
  }

  .fullscreen-mode .fixed-header {
    height: 56px !important;
    min-height: 56px !important;
  }
}

.message {
  display: flex;
  margin-bottom: 16px;
}

.user-message {
  display: flex;
  width: 100%;
  justify-content: flex-end;
  align-items: flex-end;
}

.ai-message {
  display: flex;
  width: 100%;
  justify-content: flex-start;
  align-items: flex-start;
}

.message-bubble {
  max-width: 70%;
  padding: 12px 16px;
  border-radius: 18px;
  word-wrap: break-word;
}

.user-bubble {
  background-color: #1976d2;
  color: white;
  border-bottom-right-radius: 4px;
}

.ai-bubble {
  background-color: white;
  color: #333;
  border: 1px solid #e0e0e0;
  border-bottom-left-radius: 4px;
}

.welcome-message .ai-bubble {
  max-width: 90%;
}

.extracted-filters {
  margin-top: 8px;
}

.loading-container {
  display: flex;
  flex-direction: column;
  align-items: flex-start;
  gap: 8px;
}

.loading-text {
  font-size: 14px;
  color: #666;
  font-style: italic;
  animation: fadeInOut 1.2s ease-in-out infinite;
}

.loading-dots {
  display: flex;
  align-items: center;
  gap: 4px;
}

.loading-dots span {
  width: 8px;
  height: 8px;
  border-radius: 50%;
  background-color: #666;
  animation: loading 1.4s infinite ease-in-out both;
}

.loading-dots span:nth-child(1) { animation-delay: -0.32s; }
.loading-dots span:nth-child(2) { animation-delay: -0.16s; }

@keyframes loading {
  0%, 80%, 100% {
    transform: scale(0);
  }
  40% {
    transform: scale(1);
  }
}

@keyframes fadeInOut {
  0%, 100% {
    opacity: 0.6;
  }
  50% {
    opacity: 1;
  }
}

/* Scrollbar styling */
.chat-container::-webkit-scrollbar {
  width: 6px;
}

.chat-container::-webkit-scrollbar-track {
  background: #f1f1f1;
}

.chat-container::-webkit-scrollbar-thumb {
  background: #c1c1c1;
  border-radius: 3px;
}

.chat-container::-webkit-scrollbar-thumb:hover {
  background: #a8a8a8;
}

/* Clickable chip styling */
.clickable-chip {
  cursor: pointer;
  transition: all 0.2s ease;
}

.clickable-chip:hover {
  transform: translateY(-1px);
  box-shadow: 0 2px 4px rgba(0,0,0,0.2);
}

/* Vehicle suggestion cards */
.vehicle-suggestion {
  cursor: pointer;
  transition: all 0.2s ease;
  border: 1px solid #e0e0e0;
}

.vehicle-suggestion:hover {
  border-color: #4caf50;
  box-shadow: 0 2px 8px rgba(76, 175, 80, 0.2);
  transform: translateY(-1px);
}

.vehicle-name {
  color: #2e7d32;
  font-weight: 600;
  font-size: 14px;
}

.vehicle-description {
  font-size: 12px;
  line-height: 1.3;
}

.suggested-vehicles-list {
  max-height: 300px;
  overflow-y: auto;
}

/* Microphone pulse animation */
.pulse-animation {
  animation: pulse 1.5s infinite;
}

@keyframes pulse {
  0% {
    transform: scale(1);
    box-shadow: 0 0 0 0 rgba(244, 67, 54, 0.7);
  }
  70% {
    transform: scale(1.05);
    box-shadow: 0 0 0 10px rgba(244, 67, 54, 0);
  }
  100% {
    transform: scale(1);
    box-shadow: 0 0 0 0 rgba(244, 67, 54, 0);
  }
}

/* Input area styling */
.input-area {
  min-height: 80px;
  align-items: flex-start;
}

/* Textarea container styling */
.textarea-container {
  position: relative;
  width: 100%;
  display: flex;
  flex-direction: column;
}

/* Input field styling */
.input-field {
  flex: 1;
  margin-bottom: 0 !important;
}

/* Buttons container styling */
.buttons-container {
  position: absolute;
  bottom: 8px;
  right: 12px;
  display: flex;
  gap: 4px;
  z-index: 2;
}

/* Mobile responsive input styling */
@media (max-width: 768px) {
  .input-area {
    min-height: 120px !important;
    padding: 20px 16px !important;
    align-items: flex-start !important;
  }

  .textarea-container {
    width: 100% !important;
  }

  .input-field {
    margin-bottom: 0 !important;
  }

  /* Force larger textarea on mobile */
  .input-field >>> .v-input__control {
    min-height: 80px !important;
  }

  .input-field >>> .v-input__slot {
    min-height: 80px !important;
    padding: 16px 60px 16px 20px !important;
    align-items: flex-start !important;
  }

  .input-field >>> .v-textarea textarea {
    font-size: 16px !important;
    line-height: 1.5 !important;
    padding: 4px 0 !important;
    min-height: 48px !important;
  }

  .input-field >>> .v-label {
    font-size: 14px !important;
    top: 20px !important;
    left: 20px !important;
  }

  .input-field >>> .v-label--active {
    transform: translateY(-18px) scale(0.75) !important;
    top: 20px !important;
  }

  .input-field >>> .v-text-field__details {
    display: none !important;
  }

  /* Ensure outlined style works properly */
  .input-field >>> .v-input--is-focused .v-input__slot {
    border: 2px solid #ff9800 !important;
  }

  /* Mobile buttons positioning */
  .buttons-container {
    bottom: 12px !important;
    right: 16px !important;
    gap: 6px !important;
  }
}

/* Button styling */
.mic-btn, .send-btn {
  min-width: 32px !important;
  height: 32px !important;
  background-color: rgba(255, 255, 255, 0.9) !important;
  box-shadow: 0 2px 4px rgba(0, 0, 0, 0.1) !important;
  border: 1px solid rgba(0, 0, 0, 0.1) !important;
}

.mic-btn:hover, .send-btn:hover {
  background-color: rgba(255, 255, 255, 1) !important;
  box-shadow: 0 4px 8px rgba(0, 0, 0, 0.15) !important;
}

.send-btn {
  background-color: rgba(255, 152, 0, 0.9) !important;
}

.send-btn:hover {
  background-color: rgba(255, 152, 0, 1) !important;
}

@media (max-width: 768px) {
  .mic-btn, .send-btn {
    min-width: 36px !important;
    height: 36px !important;
    margin: 0 !important;
  }

  .mic-btn .v-icon, .send-btn .v-icon {
    font-size: 18px !important;
  }
}
</style>
